import React from 'react';
import { Container } from '@mui/system';
import { faqTimeLineObj } from 'assets/animation/portalAnimation';
import { Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import { ExpandMoreIcon } from 'shared/Icons';
import { IBaseSearchRequest, IColumn, IPageModelBase } from 'models/base-type';
import { ORDER_BY_ASC, PAGE_DETAIL } from 'shared/constants/pagination';
import faqService from 'services/home/home-service';
import { Digits } from 'shared/enums/digits';
import { toast } from 'react-toastify';
import messages from 'shared/constants/messages';
import { IFaqModel } from 'models/faq';

const FAQForm: React.FC = () => {
    const totalCountRef = React.useRef(Digits.Zero);
    const pageRef = React.useRef<IPageModelBase>({ ...PAGE_DETAIL, order: ORDER_BY_ASC });
    const faqTimeLine = React.useRef(null);

    const [expandedFaq, setExpandedFaq] = React.useState<string | false>('panel1');
    const [faqs, setFaqs] = React.useState<Array<IFaqModel>>([]);

    const columns: Array<IColumn> = React.useMemo(
        () => [
            {
                name: 'Order',
                data: 'order',
            },
            {
                name: 'Question',
                data: 'question',
            },
            {
                name: 'Answer',
                data: 'answer',
            },
        ],
        [],
    );

    React.useEffect(() => {
        faqTimeLineObj(faqTimeLine);
        (async function fetchData() {
            await getFaqs();
        })();
    }, []);

    const getFaqs = async () => {
        const pageDetail = pageRef.current;
        const orderByColumnIndex = columns.findIndex((column: IColumn) => column.data === pageDetail.orderBy);
        const searchBody: IBaseSearchRequest = {
            start: pageDetail.page * pageDetail.rowsPerPage,
            length: -1,
            columns: columns,
            order: [
                {
                    column: orderByColumnIndex > -1 ? orderByColumnIndex : 0,
                    dir: pageDetail.order === ORDER_BY_ASC ? 0 : 1,
                },
            ],
        };

        await faqService
            .getFaqs(searchBody)
            .then((response) => {
                totalCountRef.current = response?.data?.data?.totalRecords;
                setFaqs(response?.data?.data?.records);
            })
            .catch(() => toast.error(messages.SomethingWentWrong));
    };

    const handleChangeFaq = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) =>
        setExpandedFaq(newExpanded ? panel : false);

    return (
        <section className="faq" ref={faqTimeLine} id="faq">
            <Container maxWidth="lg">
                <Typography variant="h3" className="section-title">
                    FAQ
                </Typography>
                <div className="accordion-block">
                    {faqs?.length > 0 ? (
                        faqs?.map((faq: IFaqModel) => {
                            return (
                                <Accordion
                                    key={faq?.order}
                                    expanded={expandedFaq === `panel${faq?.order}`}
                                    onChange={handleChangeFaq(`panel${faq?.order}`)}
                                    className="primary-accordion"
                                >
                                    <AccordionSummary
                                        aria-controls="panel1d-content"
                                        id="panel1d-header"
                                        expandIcon={<ExpandMoreIcon />}
                                    >
                                        <Typography
                                            className={expandedFaq === `panel${faq?.order}` ? '' : 'text-overflow'}
                                        >
                                            {faq?.question}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Typography
                                            dangerouslySetInnerHTML={{
                                                __html: faq?.answer ? faq?.answer : '-',
                                            }}
                                            className="break-space"
                                        />
                                    </AccordionDetails>
                                </Accordion>
                            );
                        })
                    ) : (
                        <Accordion className="primary-accordion">
                            <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
                                <Typography>{messages.NoRecordsFound}</Typography>
                            </AccordionSummary>
                        </Accordion>
                    )}
                </div>
            </Container>
        </section>
    );
};

export default React.memo(FAQForm);
